<template>
    <div v-if="configIsLoaded">
        <form
            class="form"
            @submit.prevent="handleSubmit"
            novalidate
        >
            <FormRadioGroup
                v-model="formData.tariff"
                name="tariff"
                :options="config.tariffList"
            />

            <div v-if="formData.tariff" ref="formContent">
                <fieldset class="form__fieldset">
                    <legend class="form__legend">Persönliche Daten</legend>
                    <div class="form__fieldset-fields">
                        <BaseInput
                            v-model.trim="formData.firstname"
                            label="Vorname"
                            type="text"
                            required
                            :error="formError.firstname"
                        />
                        <BaseInput
                            v-model.trim="formData.lastname"
                            label="Nachname"
                            type="text"
                            required
                            :error="formError.lastname"
                        />
                        <BaseInput
                            v-model.trim="formData.phone"
                            label="Telefon"
                            placeholder="+49123456789"
                            type="tel"
                            required
                            :error="formError.phone"
                        />
                        <BaseInput
                            v-model.trim="formData.email"
                            label="E-Mail Adresse"
                            placeholder="max@mustermann.de"
                            type="email"
                            required
                            :error="formError.email"
                        />
                    </div>
                    <div class="form__fieldset-fields form__fieldset-fields--single">
                        <BaseTextarea
                            v-model.trim="formData.message"
                            label="Mitteilung"
                        />
                    </div>
                </fieldset>

                <fieldset class="form__fieldset">
                    <legend class="form__legend">Anmietung</legend>
                    <div class="form__fieldset-fields">
                        <FormDatePicker
                            label="Abhol"
                            v-model:date-model="formData.pickupDate"
                            v-model:time-model="formData.pickupTime"
                            :time-options="pickupTimeOptions"
                            :disabled-dates="isDayClosed"
                            :closed-day-reason="getClosedDayReason"
                            :min-date="today"
                            required
                            :error="formError.pickup"
                        />

                        <FormDatePicker
                            v-if="formData.tariff === 'Individuell'"
                            label="Rückgabe"
                            v-model:date-model="formData.returnDate"
                            v-model:time-model="formData.returnTime"
                            :time-options="returnTimeOptions"
                            :disabled-dates="isDayClosed"
                            :closed-day-reason="getClosedDayReason"
                            :min-date="formData.pickupDate"
                            required
                            :error="formError.return"
                        />
                    </div>

                    <div class="form__fieldset-fields form__fieldset-fields--single">
                        <BaseCheckbox
                            v-if="config.hasCover"
                            label="Mit Plane"
                            v-model="formData.cover"
                        />
                        <BaseCheckbox
                            v-if="insuranceAvailable"
                            label="Ab einer Mietdauer von 3 Tagen und mehr, bieten wir Ihnen alternativ eine {Rücktrittsversicherung} an."
                            :label-link="config.insuranceUrl"
                            v-model="formData.insurance"
                        />
                    </div>
                </fieldset>

                <fieldset class="form__fieldset">
                    <legend class="form__legend">Sonstiges</legend>
                    <div class="form__fieldset-fields form__fieldset-fields--single">
                        <BaseCheckbox
                            label="Ich habe die {Datenschutzerklärung} gelesen und verstanden."
                            :label-link="config.termsUrl"
                            v-model="formData.terms"
                            :error="formError.terms"
                            required
                        />
                    </div>
                </fieldset>

                <div class="form__button-group">
                    <button
                        class="button-primary form__button"
                        type="submit"
                        :disabled="isSubmitting"
                    >
                    <span class="button-primary__label">
                        {{ isSubmitting ? 'Wird gesendet...' : 'Absenden' }}
                    </span>
                        <span class="button-primary__icon">
                        <BaseIcon name="ri-arrow-right-up-line" />
                    </span>
                    </button>
                </div>

            </div>
        </form>
    </div>
</template>

<script setup>
import BaseIcon from "../component/BaseIcon.vue";
import {computed, nextTick, onMounted, reactive, ref, watch} from 'vue';
import FormService from "../service/FormService";
import { OverlayService } from '../service/OverlayService';
import FormRadioGroup from "../component/FormRadioGroup.vue";
import BaseInput from "../component/BaseInput.vue";
import BaseCheckbox from "../component/BaseCheckbox.vue";
import FormDatePicker from "../component/FormDatePicker.vue";
import BaseTextarea from "../component/BaseTextarea.vue";

const isSubmitting = ref(false);
const statusMessage = ref('');
const configIsLoaded = ref(false);
const token = ref();
const formContent = ref(null);

const props = defineProps({
    id: String,
    pageUuid: String
});

const config = ref({
    termsUrl: null,
    imprintUrl: null,
    insuranceUrl: null,
    contactUrl: null,
    title: null,
    trailerVariant: null,
    hasCover: false,
    tariffList: [],
    businessHours: [],
    closingDays: []
})

const today = new Date();
today.setHours(0, 0, 0, 0);

const formData = reactive({
    terms: false,
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    message: '',
    tariff: '',
    cover: false,
    insurance: false,
    pickupDate: today,
    pickupTime: '',
    returnDate: today,
    returnTime: '',
    title: '',
    trailerVariant: '',
    groupCategory: '',
})

const formError = reactive({
    terms: null,
    firstname: null,
    lastname: null,
    phone: null,
    email: null,
    pickup: null,
    return: null
})

const loadFormConfig = async () => {
    try {
        const response = await FormService.config({
            formType: 'rental',
            pageUuid: props.pageUuid,
        });

        console.log(response.data);
        if (response.status === 200) {
            formData.title = response.data.config.title;
            formData.trailerVariant = response.data.config.trailerVariant;
            formData.groupCategory = response.data.config.groupCategory;
            token.value = response.data.config.token;
            config.value = response.data.config;

            if (isDayClosed(formData.pickupDate)) {
                formData.pickupDate = null;
            }
            if (isDayClosed(formData.returnDate)) {
                formData.returnDate = null;
            }

            configIsLoaded.value = true;
        } else {
            OverlayService.open({
                type: 'error',
                title: 'Konfigurationsfehler',
                message: 'Hoppla! Etwas ist schief gelaufen beim Laden der Formular-Konfiguration.'
            });
        }
    } catch (error) {
        OverlayService.open({
            type: 'error',
            message: 'Fehler: Verbindung zum Server fehlgeschlagen.'
        });
    } finally {
        //isSubmitting.value = false;
    }
}

// --- DATEPICKER HELPER: Disable Closed Days ---
const getClosedDayReason = (date) => {
    if (!date) return '';

    const monthDay = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const closingDay = (config.value.closingDays || []).find(entry => entry.date === monthDay);
    if (closingDay) return `Geschlossen: ${closingDay.label || 'Schließtag'}`;

    return config.value.businessHours[date.getDay()] === null
        ? 'Geschlossen: regulärer Ruhetag'
        : '';
};

const isDayClosed = (date) => Boolean(getClosedDayReason(date));

// --- TIME LOGIC HELPER ---
const getTimesForDate = (dateValue) => {
    if (!dateValue) return [];

    const date = new Date(dateValue);
    if (isDayClosed(date)) return [];

    const dayIndex = date.getDay();
    const dayConfig = config.value.businessHours[dayIndex];

    if (!dayConfig || !dayConfig.start || !dayConfig.end) return [];

    const toTotalMinutes = (timeStr) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + (m || 0);
    };

    const startTotal = toTotalMinutes(dayConfig.start);
    const endTotal = toTotalMinutes(dayConfig.end);
    const options = [];

    for (let current = startTotal; current <= endTotal; current += 15) {
        const h = Math.floor(current / 60);
        const m = current % 60;
        options.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }

    return options;
};

// --- COMPUTED OPTIONS ---
const pickupTimeOptions = computed(() => getTimesForDate(formData.pickupDate));
const returnTimeOptions = computed(() => {
    // 1. Get the standard available times for the return date
    const baseOptions = getTimesForDate(formData.returnDate);

    // 2. Check if Pickup and Return are the same day
    const isSameDay = formData.pickupDate && formData.returnDate &&
        new Date(formData.pickupDate).toDateString() === new Date(formData.returnDate).toDateString();

    // 3. If it's the same day, filter the list
    if (isSameDay && formData.pickupTime) {
        return baseOptions.filter(time => {
            // Only keep times that are strictly AFTER the pickup time
            return time > formData.pickupTime;
        });
    }

    return baseOptions;
});

// --- WATCHERS ---
watch(() => formData.pickupDate, (newPickupDate) => {
    if (formData.returnDate < newPickupDate) {
        formData.returnDate = new Date(newPickupDate);
    }
    if (!pickupTimeOptions.value.includes(formData.pickupTime)) {
        formData.pickupTime = pickupTimeOptions.value[0] || '';
    }
});

watch(() => formData.pickupTime, (newPickupTime) => {
    const isSameDay = formData.pickupDate && formData.returnDate &&
        new Date(formData.pickupDate).toDateString() === new Date(formData.returnDate).toDateString();

    if (isSameDay && formData.returnTime && formData.returnTime <= newPickupTime) {
        formData.returnTime = returnTimeOptions.value[0] || '';
    }
});

watch(() => formData.returnDate, () => {
    if (!returnTimeOptions.value.includes(formData.returnTime)) {
        formData.returnTime = returnTimeOptions.value[0] || '';
    }
});

watch(() => formData.tariff, async (newTariff) => {
    if (newTariff) {
        await nextTick();
        formContent.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (newTariff !== 'Individuell') {
        formData.returnDate = new Date(formData.pickupDate);
        formData.returnTime = '';
    }
});


const getFullDate = (dateObj, timeString) => {
    if (!dateObj || !timeString) return null;

    const combined = new Date(dateObj);
    const [hours, minutes] = timeString.split(':').map(Number);
    combined.setHours(hours, minutes, 0, 0);

    return combined;
};

const insuranceAvailable = computed(() => {
    if (formData.tariff !== 'Individuell') {
        return ['3 Tage', '7 Tage', '14 Tage'].includes(formData.tariff);
    }

    const pickup = getFullDate(formData.pickupDate, formData.pickupTime);
    const returnDate = getFullDate(formData.returnDate, formData.returnTime);

    return Boolean(pickup && returnDate && returnDate - pickup >= 3 * 24 * 60 * 60 * 1000);
});

watch(insuranceAvailable, (available) => {
    if (!available) formData.insurance = false;
});

const toLocalISO = (dateObj) => {
    const tzOffset = dateObj.getTimezoneOffset() * 60000; // offset in ms
    // Subtract the offset to "trick" ISO into showing local time
    return new Date(dateObj.getTime() - tzOffset).toISOString().slice(0, 19);
};

const mapApiErrorsToForm = (apiErrors) => {
    if (apiErrors.mainMessage) {
        OverlayService.open({
            type: 'error',
            message: apiErrors.mainMessage
        });
    }

    Object.keys(formError).forEach(fieldName => {
        if (apiErrors[fieldName]) {
            const errorValue = apiErrors[fieldName];
            formError[fieldName] = Array.isArray(errorValue) ? errorValue[0] : errorValue;
        }
    });
};

const handleSubmit = async () => {
    isSubmitting.value = true;
    statusMessage.value = '';

    Object.keys(formError).forEach(key => formError[key] = null);

    try {
        const payload = { ...formData };
        payload.insurance = insuranceAvailable.value && formData.insurance;
        
        payload.pickup = (formData.pickupDate && formData.pickupTime) 
            ? toLocalISO(getFullDate(formData.pickupDate, formData.pickupTime)) 
            : null;

        payload.return = (formData.tariff === 'Individuell' && formData.returnDate && formData.returnTime) 
            ? toLocalISO(getFullDate(formData.returnDate, formData.returnTime)) 
            : null;

        const response = await FormService.rent(payload, token.value);
        if (response.status === 200) {
            if (!response.data.success) {
                mapApiErrorsToForm(response.data.errors);
            } else {
                OverlayService.open({
                    type: 'success',
                    title: 'Erfolgreich gesendet',
                    message: response.data.success || 'Vielen Dank für Ihre Anfrage!'
                });
            }
        } else {
            OverlayService.open({
                type: 'error',
                message: 'Hoppla! Etwas ist schief gelaufen.'
            });
        }
    } catch (error) {
        console.error(error);
        OverlayService.open({
            type: 'error',
            message: 'Fehler: Verbindung zum Server fehlgeschlagen.'
        });
    } finally {
        isSubmitting.value = false;
    }
};

onMounted(() => {
    loadFormConfig();
})
</script>
